import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, type UserListParams } from "@/lib/query-keys";
import { fetchFromNextJs, httpStatusOf } from "@/lib/api/nextjs-fetch";
import { identitySync } from "@/lib/identity/identity-sync";
import { currentPathForLogin, loginUrlFor } from "@/lib/utils/login-redirect";
import type {
  CurrentUserProfileResponse,
  UsersResponse,
  GetUsersParams,
  CreateUserData,
  UpdateUserData,
  User,
} from "@/types/user";
import type { Role } from "@/types/role";

/** The body POST /api/users/me/password answers with. */
export interface ChangeOwnPasswordResponse {
  message: string;
  /** When the NEW password will itself expire, or null when expiry is off. */
  password_expires_at: string | null;
}
import { toast } from "sonner";
import { handleApiError, isAuthError } from "@/lib/utils/error-handler";
import { useTranslations } from "next-intl";

export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.users.current(),
    queryFn: () => fetchFromNextJs<CurrentUserProfileResponse>("/api/users/me"),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: false, // Don't refetch if data exists in cache
  });
}

/**
 * Changes the signed-in user's own password.
 *
 * The server ends every OTHER session and re-issues THIS one, so there is no
 * sign-out and no redirect to the login page: a user sent here by the forced
 * change simply carries on. The only thing this must do afterwards is refetch
 * /users/me, because must_change_password has just become false and the guard
 * reads it.
 *
 * Deliberately no toast on failure here: the form shows the server's refusal
 * on the field it belongs to (wrong current password, too short, unchanged).
 */
export function useChangeOwnPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      fetchFromNextJs<ChangeOwnPasswordResponse>("/api/users/me/password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.users.current(),
        refetchType: "active",
      });
    },
  });
}

export function useUserRoles(
  userId: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.users.roles(userId),
    queryFn: async () => {
      const response = await fetchFromNextJs<{
        data: { user_role_ids: number[] };
      }>(`/api/users/${userId}/roles`);
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid unnecessary requests
    enabled: options?.enabled !== false && !!userId,
  });
}

export function useUsers(params: GetUsersParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled !== false,
    queryKey: queryKeys.users.list({
      ...params,
      page: params.page ? Number(params.page) : undefined,
      page_size: params.page_size ? Number(params.page_size) : undefined,
    } as UserListParams),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      return fetchFromNextJs<UsersResponse>(
        `/api/users${queryString ? `?${queryString}` : ""}`
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

// Mutations
export function useCreateUser() {
  const queryClient = useQueryClient();
  const t = useTranslations();

  return useMutation({
    mutationFn: async (data: CreateUserData) => {
      const apiData: Record<string, unknown> = {
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        password: data.password,
        role_ids: data.roleIds,
      };
      if (data.avatarUrl !== undefined) apiData.avatar_url = data.avatarUrl;
      return fetchFromNextJs<User>("/api/users", {
        method: "POST",
        body: JSON.stringify(apiData),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success(t("settings.users.created") || "User created successfully");
    },
    onError: (error: Error) => {
      handleApiError(error, {
        t,
        showToast: true,
        skipAuthErrors: true,
      });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const t = useTranslations();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: number;
      data: UpdateUserData;
    }) => {
      const apiData: Record<string, unknown> = {};
      if (data.email) apiData.email = data.email;
      if (data.firstName) apiData.first_name = data.firstName;
      if (data.lastName) apiData.last_name = data.lastName;
      if (data.password) apiData.password = data.password;
      if (data.status) apiData.is_active = data.status === "active";
      // Send avatar_url whenever it's defined (including empty string, which
      // clears the avatar). Distinguish from "field not in patch" via
      // !== undefined rather than truthiness.
      if (data.avatarUrl !== undefined) apiData.avatar_url = data.avatarUrl;

      return fetchFromNextJs<User>(`/api/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(apiData),
      });
    },
    onSuccess: () => {
      // Invalidate users list, but use refetchType to avoid unnecessary refetches
      // Note: When used in user-drawer, invalidations are handled there to avoid duplicates
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.all,
        refetchType: "active", // Only refetch active queries
      });
      toast.success(t("settings.users.updated") || "User updated successfully");
    },
    onError: (error: Error) => {
      handleApiError(error, {
        t,
        showToast: true,
        skipAuthErrors: true,
      });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  const t = useTranslations();

  return useMutation({
    mutationFn: async (userId: number) => {
      return fetchFromNextJs(`/api/users/${userId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success(t("settings.users.deleted") || "User deleted successfully");
    },
    onError: (error: Error) => {
      handleApiError(error, {
        t,
        showToast: true,
        skipAuthErrors: true,
      });
    },
  });
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();
  const t = useTranslations();

  return useMutation({
    mutationFn: async ({
      userId,
      roleIds,
    }: {
      userId: number;
      roleIds: number[];
    }) => {
      return fetchFromNextJs<{ success: boolean }>(
        `/api/users/${userId}/roles`,
        {
          method: "PATCH",
          body: JSON.stringify({ role_ids: roleIds }),
        }
      );
    },
    onSuccess: (_, variables) => {
      // Don't invalidate here - let the caller handle it to avoid duplicate invalidations
      // when both user and roles are updated together
      toast.success(
        t("settings.users.rolesUpdated") || "User roles updated successfully"
      );
    },
    onError: (error: Error) => {
      handleApiError(error, {
        t,
        showToast: true,
        skipAuthErrors: true,
      });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  const t = useTranslations();

  return useMutation({
    mutationFn: async (userId: number) => {
      return fetchFromNextJs<User>(`/api/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: false }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success(t("settings.users.deactivated") || "User deactivated successfully");
    },
    onError: (error: Error) => {
      handleApiError(error, {
        t,
        showToast: true,
        skipAuthErrors: true,
      });
    },
  });
}

export function useReactivateUser() {
  const queryClient = useQueryClient();
  const t = useTranslations();

  return useMutation({
    mutationFn: async (userId: number) => {
      return fetchFromNextJs<User>(`/api/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: true }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success(t("settings.users.reactivated") || "User reactivated successfully");
    },
    onError: (error: Error) => {
      handleApiError(error, {
        t,
        showToast: true,
        skipAuthErrors: true,
      });
    },
  });
}

/**
 * Start acting as another user (admin only).
 *
 * Ends in the shared identity reset (lib/identity/identity-sync.ts): the saved
 * branch is forgotten, the ENTIRE query cache is dropped, the page fully
 * reloads, and every other open tab is told to do the same. A partial
 * invalidation would leave the admin looking at their own cached patient list
 * under the impersonated user's banner; a client-side navigation would leave
 * the sidebar, permissions and branch context (all derived at mount) wrapping
 * the new identity; and a tab that is not told keeps acting as the admin while
 * its requests go out as the target.
 */
export function useImpersonate() {
  const qc = useQueryClient();
  const t = useTranslations();
  return useMutation({
    mutationFn: (userId: number) =>
      fetchFromNextJs<{ id: number; email: string; roles: string[] }>(
        `/api/auth/impersonate/${userId}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      identitySync().reset(qc, {
        reason: "impersonation-started",
        broadcast: true,
        navigateTo: "/",
      });
    },
    onError: (e) => handleApiError(e, { t }),
  });
}

/** What started the stop: the banner button, or the countdown reaching 0. */
export type StopImpersonationTrigger = "switch-back" | "expired";

type StopImpersonationOutcome =
  /** 200: the backend ended the admin's live sessions (or there were none) and
   *  CLEARED the access cookie. It issues no token (review #3 M1). The reset's
   *  navigation therefore starts with no access cookie: its first requests get
   *  401 "missing authentication token" (not IMPERSONATION_ENDED), so the Next
   *  proxy runs the normal, rotating refresh on the admin's refresh cookie and
   *  the page loads as the admin. Other tabs refreshing at the same moment fall
   *  in the backend's 30s concurrent-refresh grace, not its reuse detection. */
  | "stopped"
  /** 400 from a backend older than the idempotent stop: the cookie is already
   *  a normal admin token, which is the state "stopped" leads to anyway. */
  | "not-impersonating"
  /** 401: no valid refresh session. fetchFromNextJs has already sent the
   *  browser to the login page. */
  | "session-gone";

/**
 * Return to the admin's own identity.
 *
 * Stop authenticates on the refresh cookie alone and is idempotent, so it is
 * sent with `requireAuth: false`: a 401 means there is no session to refresh
 * into, and trying would only delay the login redirect.
 *
 * Every outcome except a transport or 5xx failure on the button ends in the
 * identity reset. That one keeps the banner and shows the error, because the
 * server has not said the session ended, and re-checks /users/me in case it did.
 */
export function useStopImpersonating() {
  const qc = useQueryClient();
  const t = useTranslations();
  return useMutation<StopImpersonationOutcome, Error, StopImpersonationTrigger>({
    // The trigger is only read by the callbacks below; the request is the same.
    mutationFn: async () => {
      try {
        await fetchFromNextJs<{
          id: number;
          email: string;
          roles: string[];
          stopped?: number;
        }>("/api/auth/impersonate/stop", { method: "POST", requireAuth: false });
        return "stopped";
      } catch (e) {
        if (e instanceof Error && isAuthError(e)) return "session-gone";
        if (httpStatusOf(e) === 400) return "not-impersonating";
        throw e;
      }
    },
    // The reset below must follow promptly; a retry would only delay it.
    retry: false,
    onSuccess: (outcome, trigger) => {
      if (outcome === "session-gone") {
        identitySync().reset(qc, {
          reason: "session-ended",
          broadcast: true,
          navigateTo: loginUrlFor(currentPathForLogin()),
        });
        return;
      }
      identitySync().reset(qc, {
        reason: "impersonation-stopped",
        broadcast: true,
        // A deliberate switch-back returns to where impersonation starts; an
        // expiry reloads in place, and the missing banner shows it ended.
        navigateTo: trigger === "switch-back" ? "/settings/users" : undefined,
      });
    },
    onError: (e, trigger) => {
      if (trigger === "expired") {
        // The server-measured deadline has passed, so the impersonation token
        // is dead whatever this request said. Reloading re-reads the identity
        // the cookie actually carries.
        identitySync().reset(qc, { reason: "impersonation-stopped", broadcast: true });
        return;
      }
      handleApiError(e, { t });
      void qc.invalidateQueries({ queryKey: queryKeys.users.current(), exact: true });
    },
  });
}
